import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/app-check";

class AuthService {
  constructor() {
    this.user = null;
    this.appCheckToken = null;
    this.initialized = false;
  }

  // Initialize Firebase App Check only
  async initialize() {
    if (this.initialized) return;

    try {
      // Initialize App Check
      const appCheck = firebase.appCheck();
      const siteKey =
        process.env.VUE_APP_FIREBASE_APP_CHECK_SITE_KEY ||
        "6Lc8JOQpAAAAAPuacWadSV82r9NxD6W32vcXhase";

      appCheck.activate(siteKey, true);

      // Get App Check token
      const appCheckTokenResult = await appCheck.getToken();
      this.appCheckToken = appCheckTokenResult.token;

      this.initialized = true;
      console.log("[AuthService] Initialized successfully");
    } catch (error) {
      console.error("[AuthService] Initialization error:", error);
      throw error;
    }
  }

  // Get current Firebase user
  getCurrentUser() {
    return firebase.auth().currentUser;
  }

  // Get Firebase ID token
  async getIdToken() {
    const user = this.getCurrentUser();
    if (!user) return null;

    try {
      return await user.getIdToken();
    } catch (error) {
      console.error("[AuthService] Error getting ID token:", error);
      return null;
    }
  }

  // Get App Check token
  getAppCheckToken() {
    return this.appCheckToken;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getCurrentUser();
  }

  // Sign in with Google
  async signInWithGoogle() {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await firebase.auth().signInWithPopup(provider);
      return result.user;
    } catch (error) {
      console.error("[AuthService] Google sign-in error:", error);
      throw error;
    }
  }

  // Sign in with Microsoft
  async signInWithMicrosoft() {
    const provider = new firebase.auth.OAuthProvider("microsoft.com");
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await firebase.auth().signInWithPopup(provider);
      return result.user;
    } catch (error) {
      console.error("[AuthService] Microsoft sign-in error:", error);
      throw error;
    }
  }

  // Sign out
  async signOut() {
    try {
      await firebase.auth().signOut();
      this.user = null;
      localStorage.removeItem("userData");
      localStorage.removeItem("session");
      console.log("[AuthService] User signed out successfully");
    } catch (error) {
      console.error("[AuthService] Sign out error:", error);
      throw error;
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    return firebase.auth().onAuthStateChanged(callback);
  }

  // Check user profile completion status via API
  async checkProfileStatus() {
    const user = this.getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const idToken = await this.getIdToken();
    if (!idToken) throw new Error("No valid ID token");

    try {
      // Get base URL similar to axios.js
      const getBaseURL = () => {
        if (process.env.NODE_ENV === "development") {
          if (process.env.VUE_APP_LOCAL_API_URL) {
            return process.env.VUE_APP_LOCAL_API_URL;
          }
          return "http://127.0.0.1:5001/saludsapro/us-central1/consultasV2/api/v1/";
        }
        return "https://us-central1-saludsapro.cloudfunctions.net/consultasV2/api/v1/";
      };

      // Call your backend to check profile status
      const response = await fetch(`${getBaseURL()}login/profile-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": this.appCheckToken,
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          userLoginId: user.uid,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("[AuthService] Profile status check error:", error);
      throw error;
    }
  }

  // Get user data for localStorage
  getUserData(additionalData = {}) {
    const user = this.getCurrentUser();
    if (!user) return null;

    return {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      userLoginId: user.uid,
      ...additionalData,
    };
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
